import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTheme, getT, getBrandColors} from "./ThemeContext";
import PageHeaderIcon from "./components/PageHeaderIcon";
import API_BASE from "./apiConfig";
import {
  FaPlus, FaEdit, FaTrash, FaChevronDown, FaChevronUp,
  FaCopy, FaClone, FaTimes, FaCheck, FaBolt, FaInfoCircle, FaBan,
  FaTable,
} from "react-icons/fa";

const BASE = API_BASE;
const FONT = "'Inter','Segoe UI',sans-serif";
const BLUE = "#2563eb";
const PURPLE = "#7c3aed";

const DAY_ROWS = [1, 2, 3, 5];
const DAY_LABEL = { 1: "1-Day", 2: "2-Day", 3: "3-Day", 5: "5-Day" };
const DAY_COLOR_BASE = { 1: "#2563eb", 2: "#7c3aed", 5: "#0ea5e9" };
const dayColorOf = (days) => (days === 3 ? getBrandColors().orange : DAY_COLOR_BASE[days]);

// Periods — monthly is base (no discount), rest computed
const PERIODS = [
  { id: "monthly",     label: "Monthly",     mult: 1  },
  { id: "quarterly",   label: "Quarterly",   mult: 3  },
  { id: "semi_annual", label: "Semi-Annual", mult: 6  },
  { id: "annual",      label: "Annual",      mult: 12 },
];

const DISC_KEY = "sixers_program_discounts";
const DEFAULT_DISCOUNTS = {
  quarterly:   { type: "", value: "" },
  semi_annual: { type: "", value: "" },
  annual:      { type: "", value: "" },
};

// ─── helpers ──────────────────────────────────────────────────────────────────
const toF  = (v) => { const n = parseFloat(v); return isNaN(n) ? null : n; };
const fmt  = (v) => (v != null && v !== "" ? `$${parseFloat(v).toFixed(2)}` : "—");
const mkey = (days, levelId) => `${days}_${levelId}`;

const applyDisc = (base, type, value) => {
  const b = parseFloat(base || 0);
  const v = parseFloat(value || 0);
  if (!type || !v) return b;
  return Math.max(0, type === "pct" ? b - (b * v) / 100 : b - v);
};

const discLabel = (type, value) => {
  if (!type || !value) return null;
  return type === "pct" ? `${value}% off` : `$${parseFloat(value).toFixed(2)} off`;
};

// ══════════════════════════════════════════════════════════════════════════════
// DiscountConfig — the global discount structure panel
// ══════════════════════════════════════════════════════════════════════════════
function DiscountConfig({ discounts, onChange }) {
  const { isDark } = useTheme();
  const T = getT(isDark);

  const inputSt = {
    padding: "7px 10px", borderRadius: 7, boxSizing: "border-box",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.15)" : "#d1d5db"}`,
    background: isDark ? "rgba(255,255,255,0.07)" : "#fff",
    color: T.text, fontSize: 13, fontFamily: FONT, outline: "none",
  };

  return (
    <div style={{
      background: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.9)",
      border: `1px solid ${isDark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.08)"}`,
      borderRadius: 14, overflow: "hidden",
    }}>
      {/* Header */}
      <div style={{ padding: "14px 20px", background: isDark ? "rgba(255,255,255,0.04)" : "rgba(37,99,235,0.05)",
        borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "#e5e7eb"}`,
        display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: FONT }}>
            Discount Structure
          </div>
          <div style={{ fontSize: 11, color: T.textMuted, fontFamily: FONT, marginTop: 2 }}>
            Applied globally to all programs when generating. Saved automatically.
          </div>
        </div>
      </div>

      {/* Table */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT }}>
          <thead>
            <tr style={{ background: isDark ? "rgba(255,255,255,0.03)" : "#fafafa" }}>
              {["Period", "Multiplier", "Discount Type", "Discount Value"].map(h => (
                <th key={h} style={{ padding: "9px 14px", textAlign: "left", fontSize: 11,
                  fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em",
                  borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "#e5e7eb"}`,
                  whiteSpace: "nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PERIODS.map(p => {
              const disc   = p.id === "monthly" ? null : discounts[p.id];
              const isBase = p.id === "monthly";
              return (
                <tr key={p.id} style={{ borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.04)" : "#f5f5f5"}` }}>
                  <td style={{ padding: "10px 14px", fontWeight: 700, color: T.text, fontSize: 13 }}>
                    {p.label}
                  </td>
                  <td style={{ padding: "10px 14px", color: T.textMuted, fontSize: 12 }}>
                    × {p.mult}
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    {isBase ? (
                      <span style={{ fontSize: 12, color: T.textMuted }}>Base rate — no discount</span>
                    ) : (
                      <select value={disc?.type || ""} onChange={e => onChange(p.id, "type", e.target.value)}
                        style={{ ...inputSt, width: 140 }}>
                        <option value="">No discount</option>
                        <option value="pct">% Percentage</option>
                        <option value="amt">$ Amount off</option>
                      </select>
                    )}
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    {!isBase && disc?.type ? (
                      <input type="number" min="0" step="0.01"
                        value={disc?.value || ""}
                        onChange={e => onChange(p.id, "value", e.target.value)}
                        placeholder={disc.type === "pct" ? "e.g. 10" : "e.g. 50"}
                        style={{ ...inputSt, width: 110 }} />
                    ) : (
                      <span style={{ fontSize: 12, color: T.textMuted }}>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ProgramMatrix — the m×n grid
// ══════════════════════════════════════════════════════════════════════════════
function ProgramMatrix({ levels, programs, matrixFees, onChange, onGenerate, generating, discounts, validFrom, validTo, onValidFromChange, onValidToChange }) {
  const { isDark } = useTheme();
  const T = getT(isDark);

  // Build a lookup: (days, level_id) → existing program
  const existing = {};
  programs.forEach(p => { if (p.level_id) existing[mkey(p.days_per_week, p.level_id)] = p; });

  const filledCount = Object.values(matrixFees).filter(v => v && parseFloat(v) > 0).length;
  const newCount    = Object.entries(matrixFees).filter(([k, v]) => v && parseFloat(v) > 0 && !existing[k]).length;
  const updateCount = filledCount - newCount;

  const thSt = (col) => ({
    padding: "10px 14px", textAlign: "center", fontSize: 12, fontWeight: 700,
    color: col?.color_hex ? col.color_hex : BLUE,
    background: isDark ? "rgba(255,255,255,0.04)" : `${col?.color_hex || BLUE}10`,
    borderBottom: `2px solid ${col?.color_hex || BLUE}`,
    borderRight: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "#e5e7eb"}`,
    whiteSpace: "nowrap", fontFamily: FONT, minWidth: 130,
  });

  const rowHead = (days) => {
    const c = dayColorOf(days);
    return {
      padding: "0 14px", fontWeight: 800, fontSize: 13, color: c,
      background: isDark ? `${c}18` : `${c}0e`,
      borderRight: `2px solid ${c}`,
      textAlign: "center", fontFamily: FONT, whiteSpace: "nowrap",
    };
  };

  return (
    <div style={{
      background: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.9)",
      border: `1px solid ${isDark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.08)"}`,
      borderRadius: 14, overflow: "hidden",
    }}>
      {/* Panel header */}
      <div style={{ padding: "14px 20px", display: "flex", alignItems: "center", gap: 12,
        background: isDark ? "rgba(255,255,255,0.04)" : "rgba(37,99,235,0.05)",
        borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "#e5e7eb"}` }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: FONT }}>
            Program Matrix — Monthly Fee
          </div>
          <div style={{ fontSize: 11, color: T.textMuted, fontFamily: FONT, marginTop: 2 }}>
            Enter the monthly base fee per cell. Leave blank to skip.
            Quarterly / Semi-Annual / Annual are auto-calculated from your discount structure.
          </div>
          {/* Date range for this cost structure */}
          <div style={{ display: "flex", gap: 12, marginTop: 10, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, fontFamily: FONT,
                textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap" }}>
                Valid From
              </span>
              <input type="date" value={validFrom} onChange={e => onValidFromChange(e.target.value)}
                style={{ padding: "5px 9px", borderRadius: 7, fontSize: 12, fontFamily: FONT,
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.15)" : "#d1d5db"}`,
                  background: isDark ? "rgba(255,255,255,0.07)" : "#fff",
                  color: T.text, outline: "none" }} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, fontFamily: FONT,
                textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap" }}>
                Valid To
              </span>
              <input type="date" value={validTo} onChange={e => onValidToChange(e.target.value)}
                style={{ padding: "5px 9px", borderRadius: 7, fontSize: 12, fontFamily: FONT,
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.15)" : "#d1d5db"}`,
                  background: isDark ? "rgba(255,255,255,0.07)" : "#fff",
                  color: T.text, outline: "none" }} />
            </div>
            {(validFrom || validTo) && (
              <span style={{ fontSize: 11, color: getBrandColors().green, fontFamily: FONT }}>
                ✓ Date range will be saved with generated programs
              </span>
            )}
          </div>
        </div>
        {/* Generate button */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
          <button onClick={onGenerate} disabled={generating || filledCount === 0}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "10px 22px", borderRadius: 10, border: "none",
              background: filledCount === 0 ? (isDark ? "rgba(255,255,255,0.08)" : "#e5e7eb")
                : `linear-gradient(135deg,${BLUE},#1d4ed8)`,
              color: filledCount === 0 ? T.textMuted : "#fff",
              cursor: filledCount === 0 || generating ? "not-allowed" : "pointer",
              fontSize: 13, fontWeight: 700, fontFamily: FONT,
              boxShadow: filledCount > 0 ? "0 3px 12px rgba(37,99,235,0.4)" : "none",
              transition: "all 0.15s",
            }}>
            <FaBolt style={{ fontSize: 12 }} />
            {generating ? "Generating…" : `Generate Programs`}
          </button>
          {filledCount > 0 && (
            <div style={{ fontSize: 11, color: T.textMuted, fontFamily: FONT, textAlign: "right" }}>
              {newCount > 0 && <span style={{ color: getBrandColors().green }}>+{newCount} new</span>}
              {newCount > 0 && updateCount > 0 && <span style={{ color: T.textMuted }}> · </span>}
              {updateCount > 0 && <span style={{ color: getBrandColors().orange }}>{updateCount} update</span>}
            </div>
          )}
        </div>
      </div>

      {/* Matrix table */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", fontFamily: FONT }}>
          <thead>
            <tr>
              {/* Corner cell */}
              <th style={{ padding: "10px 14px", borderBottom: `2px solid ${isDark ? "rgba(255,255,255,0.1)" : "#e5e7eb"}`,
                borderRight: `2px solid ${isDark ? "rgba(255,255,255,0.1)" : "#e5e7eb"}`,
                background: isDark ? "rgba(255,255,255,0.03)" : "#fafafa",
                fontSize: 11, color: T.textMuted, fontWeight: 700, textAlign: "center" }}>
                Days ╲ Level
              </th>
              {levels.map(l => (
                <th key={l.level_id} style={thSt(l)}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%",
                      background: l.color_hex || BLUE, display: "block" }} />
                    {l.level_name}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {DAY_ROWS.map((days, ri) => (
              <tr key={days} style={{ background: ri % 2 === 0
                ? (isDark ? "rgba(255,255,255,0.015)" : "rgba(0,0,0,0.01)")
                : "transparent" }}>
                {/* Row header */}
                <td style={rowHead(days)}>
                  <div style={{ padding: "16px 0", display: "flex", flexDirection: "column",
                    alignItems: "center", gap: 2 }}>
                    <span style={{ fontSize: 18, fontWeight: 900 }}>{days}</span>
                    <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.04em" }}>
                      DAY{days > 1 ? "S" : ""}
                    </span>
                  </div>
                </td>
                {/* Fee cells */}
                {levels.map(l => {
                  const k   = mkey(days, l.level_id);
                  const val = matrixFees[k] || "";
                  const ex  = existing[k];
                  const fee = toF(val);
                  const hasDisc = discounts && Object.values(discounts).some(d => d.type && d.value);
                  const annualNet = fee
                    ? applyDisc(fee * 12, discounts?.annual?.type, discounts?.annual?.value)
                    : null;

                  return (
                    <td key={l.level_id} style={{
                      padding: "10px 12px", verticalAlign: "top",
                      borderRight: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "#f0f0f0"}`,
                      borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "#f0f0f0"}`,
                    }}>
                      {/* $ input */}
                      <div style={{ position: "relative" }}>
                        <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)",
                          fontSize: 12, color: T.textMuted, pointerEvents: "none" }}>$</span>
                        <input
                          type="number" min="0" step="0.01"
                          value={val}
                          onChange={e => onChange(k, e.target.value)}
                          placeholder="—"
                          style={{
                            width: "100%", padding: "8px 8px 8px 20px", borderRadius: 8,
                            boxSizing: "border-box", fontSize: 13, fontWeight: 600,
                            border: `1.5px solid ${fee
                              ? (ex ? getBrandColors().orange + "99" : getBrandColors().green + "99")
                              : (isDark ? "rgba(255,255,255,0.1)" : "#e5e7eb")}`,
                            background: fee
                              ? (ex ? (isDark ? "rgba(217,119,6,0.08)" : "rgba(217,119,6,0.05)")
                                    : (isDark ? "rgba(5,150,105,0.08)" : "rgba(5,150,105,0.05)"))
                              : (isDark ? "rgba(255,255,255,0.04)" : "#fafafa"),
                            color: T.text, fontFamily: FONT, outline: "none",
                          }}
                        />
                      </div>
                      {/* Status + preview */}
                      <div style={{ marginTop: 5, minHeight: 28 }}>
                        {fee ? (
                          <div style={{ fontSize: 10, fontFamily: FONT }}>
                            {ex ? (
                              <span style={{ color: getBrandColors().orange, fontWeight: 600 }}>↻ Updates existing</span>
                            ) : (
                              <span style={{ color: getBrandColors().green, fontWeight: 600 }}>+ New program</span>
                            )}
                            {annualNet !== null && (
                              <div style={{ color: T.textMuted, marginTop: 1 }}>
                                Annual: <span style={{ fontWeight: 700, color: hasDisc ? getBrandColors().green : T.text }}>
                                  {fmt(annualNet)}
                                </span>
                              </div>
                            )}
                          </div>
                        ) : ex ? (
                          <div style={{ fontSize: 10, color: T.textMuted, fontFamily: FONT }}>
                            Existing: {fmt(ex.fee_monthly)}/mo
                          </div>
                        ) : null}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div style={{ padding: "10px 20px", borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "#f0f0f0"}`,
        display: "flex", gap: 16, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: T.textMuted, fontFamily: FONT }}>
          <span style={{ width: 10, height: 10, borderRadius: 3, background: getBrandColors().green + "40",
            border: `1.5px solid ${getBrandColors().green}99`, display: "inline-block" }} />
          New program will be created
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: T.textMuted, fontFamily: FONT }}>
          <span style={{ width: 10, height: 10, borderRadius: 3, background: getBrandColors().orange + "40",
            border: `1.5px solid ${getBrandColors().orange}99`, display: "inline-block" }} />
          Existing program will be updated
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: T.textMuted, fontFamily: FONT }}>
          <FaInfoCircle style={{ color: BLUE }} />
          Leave a cell blank to skip it
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ProgramForm — inline create/edit form (for manual / duplicate flow)
// ══════════════════════════════════════════════════════════════════════════════
const BLANK_FORM = {
  program_name: "", days_per_week: 1, sessions_per_month: "", description: "",
  level_id: "", admin_cost: "", valid_from: "", valid_to: "", fee_monthly: "",
  disc_monthly_type: "", disc_monthly_value: "",
  disc_quarterly_type: "", disc_quarterly_value: "",
  disc_semi_type: "", disc_semi_value: "",
  disc_annual_type: "", disc_annual_value: "",
};

function ProgramForm({ initial, levels, onSave, onCancel }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const [form, setForm]   = useState(() => initial ? { ...BLANK_FORM, ...initial } : BLANK_FORM);
  const [saving, setSaving] = useState(false);
  const [err, setErr]     = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setDiscType = (periodDt, periodDv, val) =>
    setForm(f => ({ ...f, [periodDt]: val, [periodDv]: val ? f[periodDv] : "" }));

  const monthlyBase = toF(form.fee_monthly);

  const handleSubmit = async () => {
    if (!form.program_name.trim()) { setErr("Program name is required."); return; }
    if (!form.level_id) { setErr("Skill level is required."); return; }
    if (!form.fee_monthly || parseFloat(form.fee_monthly) <= 0) { setErr("Monthly fee is required."); return; }
    setSaving(true); setErr("");
    const isEdit = !!initial?.program_id;
    const res = await fetch(
      isEdit ? `${BASE}/coaching-programs/${initial.program_id}` : `${BASE}/coaching-programs/`,
      { method: isEdit ? "PUT" : "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, level_id: form.level_id || null }) }
    );
    const data = await res.json();
    setSaving(false);
    if (data.success) onSave();
    else setErr(data.message || "Save failed.");
  };

  const inp = (extra = {}) => ({
    width: "100%", padding: "8px 11px", borderRadius: 8, boxSizing: "border-box",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.15)" : "#d1d5db"}`,
    background: isDark ? "rgba(255,255,255,0.07)" : "#fff",
    color: T.text, fontSize: 13, fontFamily: FONT, outline: "none", ...extra,
  });

  const lbl = (text, req) => (
    <span style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: "0.07em",
      textTransform: "uppercase", display: "block", marginBottom: 4, fontFamily: FONT }}>
      {text}{req && <span style={{ color: getBrandColors().red }}> *</span>}
    </span>
  );

  return (
    <div style={{
      background: isDark ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.95)",
      border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.09)"}`,
      borderRadius: 14, padding: "22px 24px",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: T.text, fontFamily: FONT }}>
          {initial?.program_id ? "Edit Program" : "Create Program"}
        </span>
        <button onClick={onCancel} style={{ background: "none", border: "none", cursor: "pointer",
          color: T.textMuted, fontSize: 16 }}><FaTimes /></button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
        <div>{lbl("Program Name", true)}
          <input style={inp()} value={form.program_name} onChange={e => set("program_name", e.target.value)} /></div>
        <div>{lbl("Skill Level", true)}
          <select style={inp()} value={form.level_id} onChange={e => set("level_id", e.target.value)}>
            <option value="">Select…</option>
            {levels.map(l => <option key={l.level_id} value={l.level_id}>{l.level_name}</option>)}
          </select></div>
        <div>{lbl("Days / Week", true)}
          <select style={inp()} value={form.days_per_week} onChange={e => set("days_per_week", parseInt(e.target.value))}>
            {[1,2,3,5].map(d => <option key={d} value={d}>{d} day{d>1?"s":""}/wk</option>)}
          </select></div>
        <div>{lbl("Admin Cost ($)")}
          <input style={inp()} type="number" min="0" step="0.01" value={form.admin_cost}
            onChange={e => set("admin_cost", e.target.value)} placeholder="0.00" /></div>
      </div>

      {/* Date validity */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 12, margin: "12px 0" }}>
        <div>{lbl("Valid From")}
          <input type="date" style={inp()} value={form.valid_from || ""}
            onChange={e => set("valid_from", e.target.value)} /></div>
        <div>{lbl("Valid To")}
          <input type="date" style={inp()} value={form.valid_to || ""}
            onChange={e => set("valid_to", e.target.value)} /></div>
        <div>{lbl("Description")}
          <input style={inp()} value={form.description || ""}
            onChange={e => set("description", e.target.value)}
            placeholder="Optional notes" /></div>
      </div>

      {/* Fee columns — monthly input + 3 derived */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 12 }}>
        {PERIODS.map(p => {
          const base = monthlyBase != null ? monthlyBase * p.mult : null;
          const dtKey = p.id === "monthly" ? "disc_monthly_type"   : p.id === "quarterly" ? "disc_quarterly_type" : p.id === "semi_annual" ? "disc_semi_type" : "disc_annual_type";
          const dvKey = p.id === "monthly" ? "disc_monthly_value"  : p.id === "quarterly" ? "disc_quarterly_value": p.id === "semi_annual" ? "disc_semi_value" : "disc_annual_value";
          const dt  = form[dtKey], dv = form[dvKey];
          const net = base != null ? applyDisc(base, dt, dv) : null;
          const isM = p.id === "monthly";
          return (
            <div key={p.id} style={{
              borderRadius: 10, border: `1px solid ${isDark ? "rgba(255,255,255,0.09)" : "rgba(37,99,235,0.15)"}`,
              overflow: "hidden" }}>
              <div style={{ padding: "7px 12px", background: `${BLUE}${isDark?"33":"18"}`,
                fontSize: 11, fontWeight: 800, color: BLUE, letterSpacing: "0.07em",
                textTransform: "uppercase", fontFamily: FONT }}>
                {p.label}{p.mult > 1 && <span style={{ fontWeight: 400, opacity: 0.7 }}> × {p.mult}</span>}
              </div>
              <div style={{ padding: 10 }}>
                {lbl(isM ? "Base Fee ($)" : "Base (auto)")}
                {isM ? (
                  <input style={{ ...inp(), marginBottom: 8 }} type="number" min="0" step="0.01"
                    value={form.fee_monthly} onChange={e => set("fee_monthly", e.target.value)} placeholder="0.00" />
                ) : (
                  <div style={{ ...inp({ background: isDark ? "rgba(255,255,255,0.03)" : "#f9fafb",
                    color: base ? T.text : T.textMuted }), marginBottom: 8, userSelect: "none" }}>
                    {base != null ? `$${base.toFixed(2)}` : "—"}
                  </div>
                )}
                {lbl("Discount")}
                <select style={{ ...inp(), marginBottom: dt ? 8 : 0 }} value={dt}
                  onChange={e => setDiscType(dtKey, dvKey, e.target.value)}>
                  <option value="">None</option>
                  <option value="pct">% off</option>
                  <option value="amt">$ off</option>
                </select>
                {dt && (<>
                  {lbl(`Value ${dt === "pct" ? "(%)" : "($)"}`)}
                  <input style={{ ...inp(), marginBottom: 8 }} type="number" min="0" step="0.01"
                    value={dv} onChange={e => set(dvKey, e.target.value)} />
                </>)}
                {base != null && (
                  <div style={{ padding: "7px 10px", borderRadius: 7, textAlign: "center",
                    background: net < base ? "rgba(5,150,105,0.1)" : `${BLUE}0a` }}>
                    {net < base && <div style={{ fontSize: 10, textDecoration: "line-through",
                      color: T.textMuted, fontFamily: FONT }}>${base.toFixed(2)}</div>}
                    <div style={{ fontSize: 15, fontWeight: 800, fontFamily: FONT,
                      color: net < base ? getBrandColors().green : BLUE }}>${net.toFixed(2)}</div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {err && <div style={{ padding: "9px 13px", borderRadius: 8, background: "rgba(220,38,38,0.1)",
        color: getBrandColors().red, fontSize: 13, fontFamily: FONT, marginBottom: 12 }}>{err}</div>}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button onClick={onCancel} style={{ padding: "8px 18px", borderRadius: 8,
          border: `1px solid ${isDark ? "rgba(255,255,255,0.15)" : "#d1d5db"}`,
          background: "none", color: T.text, cursor: "pointer", fontSize: 13, fontFamily: FONT }}>
          Cancel
        </button>
        <button onClick={handleSubmit} disabled={saving} style={{
          padding: "8px 22px", borderRadius: 8, border: "none", background: BLUE, color: "#fff",
          cursor: saving ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 600, fontFamily: FONT,
          display: "flex", alignItems: "center", gap: 6, opacity: saving ? 0.7 : 1 }}>
          <FaCheck /> {saving ? "Saving…" : "Save Program"}
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════════════════════
// YearGroup — collapsible year section
// ══════════════════════════════════════════════════════════════════════════════
function YearGroup({ yr, rows, col: COL, defaultOpen, isDark, T, commonProps }) {
  const [open, setOpen] = useState(defaultOpen !== false);
  const border = `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.07)"}`;
  const colHdr = (label, width, flex) => (
    <div style={flex ? { flex: 1 } : { width, flexShrink: 0 }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: T.textMuted,
        textTransform: "uppercase", letterSpacing: "0.07em", fontFamily: FONT }}>{label}</span>
    </div>
  );
  return (
    <div style={{ borderRadius: 12, border, overflow: "visible",
      boxShadow: isDark ? "0 2px 12px rgba(0,0,0,0.2)" : "0 2px 10px rgba(0,0,0,0.04)" }}>

      {/* Year header — clickable toggle */}
      <div onClick={() => setOpen(o => !o)} style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "8px 14px", cursor: "pointer",
        background: isDark ? "rgba(37,99,235,0.14)" : "rgba(37,99,235,0.07)",
        borderRadius: open ? "12px 12px 0 0" : 12,
        userSelect: "none",
      }}>
        <span style={{ fontSize: 14, fontWeight: 800, color: BLUE, fontFamily: FONT, letterSpacing: "0.05em" }}>{yr}</span>
        <span style={{ fontSize: 11, color: T.textMuted, fontFamily: FONT }}>
          · {rows.length} program{rows.length !== 1 ? "s" : ""}
        </span>
        <span style={{ marginLeft: "auto", color: BLUE, fontSize: 11 }}>
          {open ? <FaChevronUp /> : <FaChevronDown />}
        </span>
      </div>

      {/* Column header row + data rows — shown only when open */}
      {open && (
        <>
          <div style={{
            display: "flex", alignItems: "center", padding: "5px 14px",
            background: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
            borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)"}`,
            borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)"}`,
          }}>
            <div style={{ width: COL.badge, flexShrink: 0 }} />
            {colHdr("Program", COL.name - 26)}
            {colHdr("Level", COL.level)}
            {colHdr("Monthly", COL.monthly)}
            {colHdr("Annual", 0, true)}
          </div>
          {rows.map(p => (
            <ProgramCard key={p.program_id} prog={p} col={COL} {...commonProps} />
          ))}
        </>
      )}
    </div>
  );
}

// ProgramRow — single list row, grouped by year outside
// ══════════════════════════════════════════════════════════════════════════════
function ProgramCard({ prog, col, allPrograms, levels, onEdit, onDuplicate, onCloneToLevel, onDelete }) {
  const COL = col || { badge: 44, name: 200, level: 110, monthly: 110, annual: 110, actions: 220 };
  const { isDark } = useTheme();
  const T = getT(isDark);
  const [expanded, setExpanded] = useState(false);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloning, setCloning]     = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setCloneOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const levelColor = prog.level_color || BLUE;
  const dayColor   = dayColorOf(prog.days_per_week) || BLUE;

  const rows = PERIODS.map(p => ({
    ...p, fee: prog[p.feeKey || `fee_${p.id}`],
    dt: prog[p.id === "monthly" ? "disc_monthly_type" : p.id === "quarterly" ? "disc_quarterly_type" : p.id === "semi_annual" ? "disc_semi_type" : "disc_annual_type"],
    dv: prog[p.id === "monthly" ? "disc_monthly_value": p.id === "quarterly" ? "disc_quarterly_value": p.id === "semi_annual" ? "disc_semi_value" : "disc_annual_value"],
  })).filter(r => r.fee != null && parseFloat(r.fee) > 0);

  const usedLevelIds = new Set(
    allPrograms
      .filter(p => p.days_per_week === prog.days_per_week && p.program_id !== prog.program_id
        && (prog.valid_from || "").slice(0,4) === (p.valid_from || "").slice(0,4))
      .map(p => p.level_id)
  );
  const availableLevels = levels.filter(l => !usedLevelIds.has(l.level_id));

  const handleClone = async (level) => {
    setCloning(level.level_id);
    await onCloneToLevel(prog, level);
    setCloning(null);
    setCloneOpen(false);
  };

  const iconBtn = (onClick, title, color, icon, label) => (
    <button onClick={onClick} title={title} style={{
      background: "none", border: "none", cursor: "pointer", color, padding: "4px 7px",
      borderRadius: 6, fontSize: 13, display: "flex", alignItems: "center", gap: 3, fontFamily: FONT,
    }}>{icon}{label && <span style={{ fontSize: 11 }}>{label}</span>}</button>
  );

  const border = `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)"}`;

  return (
    <div style={{ borderBottom: border }}>
      {/* Main row */}
      <div style={{ display: "flex", alignItems: "center", padding: "9px 14px",
        background: isDark ? "rgba(255,255,255,0.01)" : "transparent",
      }}>
        {/* Day badge — width matches COL.badge */}
        <div style={{ width: COL.badge, flexShrink: 0, display: "flex", alignItems: "center" }}>
          <div style={{ width: 32, height: 32, borderRadius: 8,
            background: `${dayColor}18`, display: "flex", alignItems: "center", justifyContent: "center",
            border: `1.5px solid ${dayColor}40` }}>
            <span style={{ fontSize: 11, fontWeight: 900, color: dayColor, fontFamily: FONT }}>
              {prog.days_per_week}d
            </span>
          </div>
        </div>

        {/* Program name — COL.name minus badge overlap */}
        <div style={{ width: COL.name - 26, flexShrink: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.text, fontFamily: FONT,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {prog.program_name}
          </div>
        </div>

        {/* Level chip — COL.level */}
        <div style={{ width: COL.level, flexShrink: 0 }}>
          {prog.level_name && (
            <span style={{ fontSize: 10, fontWeight: 700, color: levelColor,
              background: `${levelColor}18`, padding: "2px 8px", borderRadius: 8, fontFamily: FONT }}>
              {prog.level_name}
            </span>
          )}
        </div>

        {/* Monthly — COL.monthly */}
        <div style={{ width: COL.monthly, flexShrink: 0 }}>
          {prog.fee_monthly && (
            <span style={{ fontSize: 12, color: T.textMuted, fontFamily: FONT, whiteSpace: "nowrap" }}>
              <span style={{ fontWeight: 700, color: T.text }}>{fmt(prog.fee_monthly)}</span>/mo
            </span>
          )}
        </div>

        {/* Annual — COL.annual */}
        <div style={{ flex: 1, flexShrink: 0 }}>
          {prog.fee_annual && (
            <span style={{ fontSize: 12, color: T.textMuted, fontFamily: FONT, whiteSpace: "nowrap" }}>
              <span style={{ fontWeight: 700, color: getBrandColors().green }}>
                {fmt(applyDisc(prog.fee_annual, prog.disc_annual_type, prog.disc_annual_value))}
              </span>/yr
            </span>
          )}
        </div>

        {/* Actions — right side */}
        <div style={{ display: "flex", gap: 0, alignItems: "center", flexShrink: 0 }}>
          {iconBtn(() => onEdit(prog), "Edit", BLUE, <FaEdit />)}
          {iconBtn(() => onDuplicate(prog), "Duplicate", PURPLE, <FaCopy />)}

          {/* Clone popover */}
          <div style={{ position: "relative" }} ref={ref}>
            <button onClick={() => setCloneOpen(o => !o)} title="Clone to level" style={{
              padding: "4px 7px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 13,
              background: cloneOpen ? `${getBrandColors().green}20` : "none", color: getBrandColors().green, fontFamily: FONT,
              display: "flex", alignItems: "center", gap: 3,
            }}>
              <FaClone /><span style={{ fontSize: 11 }}>Clone</span>
            </button>
            {cloneOpen && (
              <div style={{
                position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 300,
                minWidth: 190, borderRadius: 10, overflow: "hidden",
                background: isDark ? "#1e293b" : "#fff",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)"}`,
                boxShadow: "0 8px 28px rgba(0,0,0,0.18)",
              }}>
                <div style={{ padding: "8px 12px 5px", fontSize: 10, fontWeight: 700,
                  color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", fontFamily: FONT,
                  borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "#f0f0f0"}` }}>
                  Clone to level
                </div>
                {availableLevels.length === 0 ? (
                  <div style={{ padding: "10px 12px", fontSize: 12, color: T.textMuted, fontFamily: FONT }}>
                    All levels covered for {prog.days_per_week}-day.
                  </div>
                ) : availableLevels.map(l => (
                  <button key={l.level_id} onClick={() => handleClone(l)} disabled={cloning === l.level_id}
                    style={{ width: "100%", textAlign: "left", padding: "8px 12px", border: "none",
                      background: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 7,
                      fontFamily: FONT,
                      borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.04)" : "#f8f8f8"}` }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: l.color_hex || BLUE, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: T.text }}>{l.level_name}</span>
                    {cloning === l.level_id && <span style={{ fontSize: 11, color: T.textMuted, marginLeft: "auto" }}>…</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {iconBtn(() => onDelete(prog), "Deactivate", getBrandColors().red, <FaTrash />)}
          <button onClick={() => setExpanded(e => !e)} title="Show fee breakdown" style={{
            background: "none", border: "none", cursor: "pointer", color: T.textMuted,
            padding: "4px 6px", fontSize: 12,
          }}>{expanded ? <FaChevronUp /> : <FaChevronDown />}</button>
        </div>
      </div>

      {/* Expanded fee breakdown */}
      {expanded && rows.length > 0 && (
        <div style={{ background: isDark ? "rgba(0,0,0,0.15)" : "#f8fafc",
          borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "#eef0f3"}`, padding: "0 0 4px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, fontFamily: FONT }}>
            <thead>
              <tr>
                {["Period","Base","Discount","Net Price","You Save"].map(h => (
                  <th key={h} style={{ padding: "6px 14px", textAlign: "left", color: T.textMuted,
                    fontWeight: 700, fontSize: 10, textTransform: "uppercase",
                    borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "#eaedf0"}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const base = parseFloat(r.fee);
                const net  = applyDisc(base, r.dt, r.dv);
                const save = base - net;
                return (
                  <tr key={r.id}>
                    <td style={{ padding: "5px 14px", fontWeight: 600, color: T.text }}>{r.label}</td>
                    <td style={{ padding: "5px 14px", color: T.textMuted }}>${base.toFixed(2)}</td>
                    <td style={{ padding: "5px 14px", color: r.dt ? getBrandColors().orange : T.textMuted }}>
                      {r.dt === "pct" && r.dv ? `-${r.dv}%` : r.dt === "amt" && r.dv ? `-$${parseFloat(r.dv).toFixed(2)}` : "—"}
                    </td>
                    <td style={{ padding: "5px 14px", fontWeight: 700, color: save > 0 ? getBrandColors().green : T.text }}>${net.toFixed(2)}</td>
                    <td style={{ padding: "5px 14px", color: save > 0 ? getBrandColors().orange : T.textMuted }}>
                      {save > 0 ? `$${save.toFixed(2)}` : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// CoachingProgram — main page
// ══════════════════════════════════════════════════════════════════════════════
export default function CoachingProgram() {
  const { isDark } = useTheme();
  const T = getT(isDark);

  const [programs,  setPrograms]  = useState([]);
  const [levels,    setLevels]    = useState([]);
  const [loading,   setLoading]   = useState(true);

  // Matrix state — always starts empty (no pre-fill from DB)
  const [matrixFees,  setMatrixFees]  = useState({});
  const [matrixFrom,  setMatrixFrom]  = useState("");
  const [matrixTo,    setMatrixTo]    = useState("");

  // Discount state — persisted in localStorage
  const [discounts, setDiscounts] = useState(() => {
    try { return JSON.parse(localStorage.getItem(DISC_KEY)) || DEFAULT_DISCOUNTS; }
    catch { return DEFAULT_DISCOUNTS; }
  });

  // Form state
  const [showForm,   setShowForm]   = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  // UI
  const [generating, setGenerating] = useState(false);
  const [msg, setMsg] = useState({ ok: true, text: "" });
  const [filterDays,  setFilterDays]  = useState(0);
  const [filterLevel, setFilterLevel] = useState(0);

  const toast = (ok, text, ms = 4000) => {
    setMsg({ ok, text });
    setTimeout(() => setMsg({ ok: true, text: "" }), ms);
  };

  const load = useCallback(async () => {
    setLoading(true);
    const [pr, lr] = await Promise.allSettled([
      fetch(`${BASE}/coaching-programs/`).then(r => r.json()),
      fetch(`${BASE}/config/lookups/skill-levels`).then(r => r.json()),
    ]);
    const progs = pr.status === "fulfilled" ? (pr.value.programs || []) : [];
    const lvls  = lr.status === "fulfilled" ? ((lr.value.data || []).filter(l => l.active !== 0)) : [];
    setPrograms(progs);
    setLevels(lvls);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Persist discounts to localStorage whenever they change
  useEffect(() => {
    try { localStorage.setItem(DISC_KEY, JSON.stringify(discounts)); } catch {}
  }, [discounts]);

  const handleDiscountChange = (period, field, val) => {
    setDiscounts(d => ({
      ...d,
      [period]: { ...d[period], [field]: val, ...(field === "type" && !val ? { value: "" } : {}) },
    }));
  };

  const handleGenerate = async () => {
    const cells = [];
    for (const [key, fee] of Object.entries(matrixFees)) {
      if (!fee || parseFloat(fee) <= 0) continue;
      const [days, levelId] = key.split("_");
      cells.push({ days_per_week: parseInt(days), level_id: parseInt(levelId), fee_monthly: fee });
    }
    if (cells.length === 0) { toast(false, "Fill in at least one cell first."); return; }
    setGenerating(true);
    const res  = await fetch(`${BASE}/coaching-programs/bulk-generate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        cells, discounts,
        valid_from: matrixFrom || null,
        valid_to:   matrixTo   || null,
      }),
    });
    const data = await res.json();
    setGenerating(false);
    if (data.success) {
      setMatrixFees({});   // clear matrix after successful generate
      load();
      const parts = [];
      if (data.created) parts.push(`${data.created} created`);
      if (data.updated) parts.push(`${data.updated} updated`);
      if (!data.created && !data.updated) parts.push("no changes — fees unchanged");
      toast(true, `Programs generated: ${parts.join(", ")}.`);
    } else {
      toast(false, data.message || "Generate failed.");
    }
  };

  const handleDeactivateAll = async () => {
    if (!window.confirm(`Deactivate ALL ${programs.length} active programs? This cannot be undone without re-generating.`)) return;
    const res  = await fetch(`${BASE}/coaching-programs/bulk-deactivate`, { method: "DELETE" });
    const data = await res.json();
    if (data.success) {
      setMatrixFees({});
      load();
      toast(true, `${data.deactivated} program${data.deactivated !== 1 ? "s" : ""} deactivated.`);
    } else {
      toast(false, data.message || "Failed.");
    }
  };

  const handleDuplicate = (prog) => {
    setEditTarget({ ...prog, program_id: null, program_name: `${prog.program_name} (Copy)`, level_id: "" });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCloneToLevel = async (prog, level) => {
    const name = `${prog.days_per_week}-Day ${level.level_name}`;
    const res  = await fetch(`${BASE}/coaching-programs/${prog.program_id}/clone`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ level_id: level.level_id, program_name: name }),
    });
    const data = await res.json();
    if (data.success) { load(); toast(true, `"${name}" created.`); }
    else toast(false, data.message || "Clone failed.");
  };

  const handleDelete = async (prog) => {
    if (!window.confirm(`Deactivate "${prog.program_name}"?`)) return;
    const res  = await fetch(`${BASE}/coaching-programs/${prog.program_id}`, { method: "DELETE" });
    const data = await res.json();
    if (data.success) { load(); toast(true, "Program deactivated."); }
    else toast(false, data.message || "Failed.");
  };

  const filtered = programs.filter(p =>
    (filterDays  === 0 || p.days_per_week === filterDays) &&
    (filterLevel === 0 || p.level_id === filterLevel)
  );

  const [activeTab, setActiveTab] = useState("matrix");

  const page = {
    minHeight: "100dvh", padding: "24px 28px", fontFamily: FONT,
    background: isDark
      ? "linear-gradient(135deg,#0f172a 0%,#1e293b 100%)"
      : "linear-gradient(135deg,#f0f4ff 0%,#f8faff 100%)",
  };

  const TABS = [
    { id: "matrix",   label: "Program Matrix" },
    { id: "discount", label: "Discount" },
    { id: "programs", label: `Existing Programs${programs.length ? ` (${programs.length})` : ""}` },
  ];

  const commonProps = {
    allPrograms: programs, levels,
    onEdit: prog => {
      setEditTarget(prog); setShowForm(true);
      setActiveTab("programs");
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    onDuplicate: handleDuplicate,
    onCloneToLevel: handleCloneToLevel,
    onDelete: handleDelete,
  };

  return (
    <div style={page}>
      {/* ── Page header ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <PageHeaderIcon icon={<FaTable size={20} color={getBrandColors().blueDark} />} />
          <div>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: T.text, fontFamily: FONT }}>
              Coaching Programs
            </h1>
            <p style={{ margin: "3px 0 0", fontSize: 12, color: T.textMuted, fontFamily: FONT }}>
              Build the fee matrix, set global discounts, generate programs in one click
            </p>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {programs.length > 0 && (
            <button onClick={handleDeactivateAll} style={{
              display: "flex", alignItems: "center", gap: 7, padding: "9px 16px", borderRadius: 10,
              border: `1.5px solid ${getBrandColors().red}`, background: "none",
              color: getBrandColors().red, cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: FONT,
            }}>
              <FaBan size={11} /> Deactivate All
            </button>
          )}
          <button onClick={() => { setEditTarget(null); setShowForm(s => !s); setActiveTab("programs"); }} style={{
            display: "flex", alignItems: "center", gap: 7, padding: "9px 18px", borderRadius: 10,
            border: `1.5px solid ${BLUE}`, background: showForm && !editTarget ? `${BLUE}15` : "none",
            color: BLUE, cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: FONT,
          }}>
            <FaPlus size={11} /> Create Manually
          </button>
        </div>
      </div>

      {/* Toast */}
      {msg.text && (
        <div style={{ marginBottom: 14, padding: "10px 16px", borderRadius: 8,
          background: msg.ok ? "rgba(5,150,105,0.12)" : "rgba(220,38,38,0.12)",
          color: msg.ok ? getBrandColors().green : getBrandColors().red, fontSize: 13, fontFamily: FONT,
          display: "flex", alignItems: "center", gap: 8 }}>
          {msg.ok ? <FaCheck /> : <FaTimes />} {msg.text}
        </div>
      )}

      {/* ── Tab bar ── */}
      <div style={{
        display: "flex", gap: 0, marginBottom: 20,
        borderBottom: `2px solid ${isDark ? "rgba(255,255,255,0.08)" : "#e5e7eb"}`,
      }}>
        {TABS.map(t => {
          const active = activeTab === t.id;
          return (
            <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
              padding: "10px 22px", border: "none", cursor: "pointer", fontFamily: FONT,
              fontSize: 13, fontWeight: active ? 700 : 500,
              color: active ? BLUE : T.textMuted,
              background: "none",
              borderBottom: active ? `2px solid ${BLUE}` : "2px solid transparent",
              marginBottom: -2,
              transition: "all 0.15s",
            }}>{t.label}</button>
          );
        })}
      </div>

      {/* ── TAB 1: Program Matrix ── */}
      {activeTab === "matrix" && (
        <div>
          {loading ? (
            <div style={{ textAlign: "center", padding: 40, color: T.textMuted, fontFamily: FONT }}>Loading…</div>
          ) : levels.length === 0 ? (
            <div style={{ padding: 24, borderRadius: 14, textAlign: "center", color: T.textMuted, fontFamily: FONT,
              background: isDark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.8)",
              border: `1px dashed ${isDark ? "rgba(255,255,255,0.1)" : "#d1d5db"}` }}>
              No skill levels found. Add them in <strong>Config Master → Lookup Values → Skill Levels</strong> first.
            </div>
          ) : (
            <ProgramMatrix
              levels={levels}
              programs={programs}
              matrixFees={matrixFees}
              discounts={discounts}
              generating={generating}
              validFrom={matrixFrom}
              validTo={matrixTo}
              onValidFromChange={setMatrixFrom}
              onValidToChange={setMatrixTo}
              onChange={(k, v) => setMatrixFees(f => ({ ...f, [k]: v }))}
              onGenerate={async () => { await handleGenerate(); setActiveTab("programs"); }}
            />
          )}
        </div>
      )}

      {/* ── TAB 2: Discount ── */}
      {activeTab === "discount" && (
        <DiscountConfig discounts={discounts} onChange={handleDiscountChange} />
      )}

      {/* ── TAB 3: Existing Programs ── */}
      {activeTab === "programs" && (
        <div>
          {/* Manual form */}
          {showForm && (
            <div style={{ marginBottom: 24 }}>
              <ProgramForm
                initial={editTarget}
                levels={levels}
                onSave={() => {
                  setShowForm(false); setEditTarget(null); load();
                  toast(true, editTarget?.program_id ? "Program updated." : "Program created.");
                }}
                onCancel={() => { setShowForm(false); setEditTarget(null); }}
              />
            </div>
          )}

          {/* Filters */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
            {[0,...DAY_ROWS].map(d => (
              <button key={d} onClick={() => setFilterDays(d)} style={{
                padding: "4px 12px", borderRadius: 16, border: "none", cursor: "pointer",
                fontSize: 11, fontWeight: 600, fontFamily: FONT,
                background: filterDays === d ? BLUE : (isDark ? "rgba(255,255,255,0.08)" : "#e5e7eb"),
                color: filterDays === d ? "#fff" : T.textMuted,
              }}>{d === 0 ? "All" : `${d}-Day`}</button>
            ))}
            <div style={{ width: 1, height: 16, background: isDark ? "rgba(255,255,255,0.1)" : "#d1d5db" }} />
            <button onClick={() => setFilterLevel(0)} style={{
              padding: "4px 12px", borderRadius: 16, border: "none", cursor: "pointer",
              fontSize: 11, fontWeight: 600, fontFamily: FONT,
              background: filterLevel === 0 ? BLUE : (isDark ? "rgba(255,255,255,0.08)" : "#e5e7eb"),
              color: filterLevel === 0 ? "#fff" : T.textMuted,
            }}>All Levels</button>
            {levels.map(l => (
              <button key={l.level_id} onClick={() => setFilterLevel(l.level_id)} style={{
                padding: "4px 12px", borderRadius: 16, border: "none", cursor: "pointer",
                fontSize: 11, fontWeight: 600, fontFamily: FONT,
                background: filterLevel === l.level_id ? (l.color_hex || BLUE) : (isDark ? "rgba(255,255,255,0.08)" : "#e5e7eb"),
                color: filterLevel === l.level_id ? "#fff" : T.textMuted,
              }}>{l.level_name}</button>
            ))}
          </div>

          {programs.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: T.textMuted, fontFamily: FONT,
              background: isDark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.8)",
              borderRadius: 14, border: `1px dashed ${isDark ? "rgba(255,255,255,0.1)" : "#d1d5db"}` }}>
              No programs yet. Go to <strong>Program Matrix</strong> tab to generate.
            </div>
          ) : (() => {
            const byYear = {};
            filtered.forEach(p => {
              const yr = p.valid_from ? p.valid_from.slice(0, 4) : "No Year";
              if (!byYear[yr]) byYear[yr] = [];
              byYear[yr].push(p);
            });
            const years = Object.keys(byYear).sort((a, b) => b.localeCompare(a));

            // Fixed column widths — must match ProgramCard row layout exactly
            const COL = { badge: 44, name: 200, level: 110, monthly: 110, annual: 110 };

            return (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {years.map((yr, i) => (
                  <YearGroup key={yr} yr={yr} rows={byYear[yr]} col={COL}
                    defaultOpen={i === 0} isDark={isDark} T={T} commonProps={commonProps} />
                ))}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
